import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Shield, Check, X, BadgeCheck, BadgeX } from "lucide-react";
import { getStatusBadge } from "./ClaimsUtils";
import { NursingHomeClaim } from "@/types/admin";

interface ClaimDetailsCardProps {
  claim: NursingHomeClaim;
  adminNotes: string;
  setAdminNotes: (s: string) => void;
  onApprove: () => void;
  onReject: () => void;
  onClose: () => void;
}

const ClaimDetailsCard: React.FC<ClaimDetailsCardProps> = ({
  claim,
  adminNotes,
  setAdminNotes,
  onApprove,
  onReject,
  onClose,
}) => (
  <Card className="bg-white shadow-sm border p-8 mb-8">
    <CardHeader className="pb-6">
      <div className="flex items-start justify-between">
        <CardTitle className="text-2xl font-bold flex items-center gap-2">
          <Shield className="h-6 w-6 text-blue-500" /> Claim Details
        </CardTitle>
        {getStatusBadge(claim.status)}
      </div>
      <CardDescription className="text-base mt-2">
        Submitted on {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : ""}
      </CardDescription>
    </CardHeader>
    <CardContent className="space-y-8 divide-y divide-muted">
      <div className="pb-8">
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <Shield className="h-5 w-5 text-blue-500" /> Nursing Home
        </h3>
        <p className="text-base">{claim.nursing_home?.center_name}</p>
        <p className="text-sm text-muted-foreground">{claim.nursing_home?.county}</p>
      </div>

      <div className="pt-8 pb-8">
        <h3 className="text-lg font-semibold mb-1">Claimant Information</h3>
        <p className="text-base">
          {(claim.user?.user_metadata?.first_name || "") + " " + (claim.user?.user_metadata?.last_name || "")}
        </p>
        <p className="text-sm text-muted-foreground">{claim.user?.email}</p>
      </div>

      <div className="pt-8 pb-8">
        <h3 className="text-lg font-semibold mb-1">Role & Contact</h3>
        <p className="text-base">{claim.verification_documents?.role || "Not specified"}</p>
        <p className="text-sm text-muted-foreground">
          Phone: {claim.verification_documents?.contactPhone || "Not provided"}
        </p>
      </div>

      {claim.notes && (
        <div className="pt-8 pb-8">
          <h3 className="text-lg font-semibold mb-1">Claimant Notes</h3>
          <p className="text-base bg-muted/20 p-4 rounded">{claim.notes}</p>
        </div>
      )}

      {claim.status === "pending" && (
        <div className="pt-8">
          <Label htmlFor="adminNotes" className="text-base font-semibold">Admin Notes</Label>
          <Textarea
            id="adminNotes"
            placeholder="Add notes about this claim (optional)"
            className="mt-2"
            value={adminNotes}
            onChange={e => setAdminNotes(e.target.value)}
          />
        </div>
      )}
    </CardContent>
    <CardFooter className={`flex ${claim.status === "pending" ? "justify-between" : "justify-end"} pt-8`}> 
      {claim.status === "pending" && (
        <>
          <Button
            variant="destructive"
            onClick={onReject}
          >
            <X className="h-4 w-4 mr-1" /> Reject
          </Button>
          <Button
            variant="default"
            onClick={onApprove}
          >
            <Check className="h-4 w-4 mr-1" /> Approve
          </Button>
        </>
      )}
      <Button
        variant="outline"
        onClick={onClose}
      >
        Close
      </Button>
    </CardFooter>
  </Card>
);

export default ClaimDetailsCard;
